import { FC, useState } from 'react'
import { KTIcon } from '../../../../../_metronic/helpers'
import InfoHint from '../../../../common/InfoHint'
import { getErrorCode, getErrorResponse } from '../../../../common/api/errors'
import { setMerchantApiIpAllowlist } from './api'

interface Props {
  merchantId: number
  enabled: boolean
  entries: string[]
}

type Saved = { enabled: boolean; entries: string[] }

const HINT =
  'Запросы интеграции мерчанта принимаются только с перечисленных адресов. Платёжная страница, колбэки и вход в кабинет от списка не зависят.'
const EMPTY_LIST = 'Чтобы включить проверку, добавьте хотя бы один адрес.'

const toText = (entries: string[]) => entries.join('\n')
const toEntries = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')

/** The textarea line (from 1) of each entry sent: blank lines are skipped when sending, not when reading. */
const lineNumbers = (text: string): number[] =>
  text.split('\n').reduce<number[]>((lines, line, index) => (line.trim() === '' ? lines : [...lines, index + 1]), [])

const refusalText = (error: unknown, sent: string[], text: string): string => {
  const code = getErrorCode(error)
  if (code === 'IP_ALLOWLIST_ENTRY_INVALID') {
    const field = Object.keys(getErrorResponse<Record<string, string>>(error) ?? {}).find((key) => key.startsWith('entries.'))
    const index = field ? Number(field.slice('entries.'.length)) : NaN
    const line = lineNumbers(text)[index]

    return Number.isInteger(index) && sent[index] !== undefined && line !== undefined
      ? `Строка ${line}: «${sent[index]}» — не IP-адрес и не подсеть CIDR.`
      : 'В списке есть значение, которое не является IP-адресом или подсетью CIDR.'
  }
  if (code === 'IP_ALLOWLIST_TOO_LONG') {
    return 'В списке не может быть больше 100 записей.'
  }
  if (code === 'IP_ALLOWLIST_EMPTY') {
    return EMPTY_LIST
  }

  return 'Не удалось сохранить белый список.'
}

/**
 * The API IP allowlist of a merchant: the addresses its integration may call the API from, and the
 * switch that enforces them. The list and the switch are saved together.
 */
const MerchantIpAllowlist: FC<Props> = ({ merchantId, enabled, entries }) => {
  const [saved, setSaved] = useState<Saved>({ enabled, entries })
  const [text, setText] = useState(toText(entries))
  const [on, setOn] = useState(enabled)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const draft = toEntries(text)
  const empty = draft.length === 0
  const dirty = on !== saved.enabled || text !== toText(saved.entries)

  const onText = (value: string) => {
    setText(value)
    setError(null)
    if (toEntries(value).length === 0) {
      // an enforced empty list would refuse every request: the switch goes off with the last address
      setOn(false)
    }
  }

  const reset = () => {
    setText(toText(saved.entries))
    setOn(saved.enabled)
    setError(null)
  }

  const apply = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await setMerchantApiIpAllowlist(merchantId, { enabled: on, entries: draft })
      if (!res?.data?.success) {
        throw res
      }

      const merchant = res.data.merchant
      const next = { enabled: merchant.apiIpAllowlistEnabled ?? false, entries: merchant.apiIpAllowlist ?? [] }
      setSaved(next)
      setText(toText(next.entries))
      setOn(next.enabled)
    } catch (e) {
      setError(refusalText(e, draft, text))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-7'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-center'>
          <span className='card-label fw-bold fs-3 mb-1'>Белый список IP для API</span>
          <InfoHint text={HINT} />
        </h3>
        <div className='card-toolbar gap-2'>
          {saving && (
            <span className='text-muted d-inline-flex align-items-center gap-2'>
              <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
              Сохраняем...
            </span>
          )}
          {dirty && !saving && (
            <>
              <button type='button' className='btn btn-sm btn-icon btn-light-success' title='Применить' onClick={apply}>
                <KTIcon iconName='check' className='fs-2' />
              </button>
              <button type='button' className='btn btn-sm btn-icon btn-light-danger' title='Сбросить' onClick={reset}>
                <KTIcon iconName='cross' className='fs-2' />
              </button>
            </>
          )}
        </div>
      </div>
      <div className='card-body py-4'>
        <div className='form-check form-switch form-switch-sm form-check-custom mb-2'>
          <input
            id={`merchant_ip_allowlist_enabled_${merchantId}`}
            className='form-check-input'
            type='checkbox'
            checked={on}
            disabled={saving || empty}
            onChange={(event) => setOn(event.target.checked)}
          />
          <label className='form-check-label fw-semibold' htmlFor={`merchant_ip_allowlist_enabled_${merchantId}`}>
            Проверять адрес запросов API
          </label>
        </div>
        {empty && <div className='text-muted fs-7 mb-4'>{EMPTY_LIST}</div>}

        <label className='form-label fw-semibold mt-3' htmlFor={`merchant_ip_allowlist_entries_${merchantId}`}>
          Адреса и подсети
        </label>
        <textarea
          id={`merchant_ip_allowlist_entries_${merchantId}`}
          className='form-control font-monospace'
          style={{ maxWidth: 420 }}
          rows={Math.min(12, Math.max(4, text.split('\n').length + 1))}
          placeholder={'203.0.113.7\n198.51.100.0/24\n2001:db8::/32'}
          spellCheck={false}
          value={text}
          disabled={saving}
          onChange={(event) => onText(event.target.value)}
        />
        <div className='text-muted fs-8 mt-1'>
          По одному значению в строке: адрес IPv4 или IPv6, либо подсеть CIDR. Не больше 100 записей.
        </div>
        {error && <div className='text-danger mt-2'>{error}</div>}
      </div>
    </div>
  )
}

export default MerchantIpAllowlist
